import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardHeader from "../../components/app_components/DashboardHeader";
import DashboardSidebar from "../../components/app_components/DashboardSidebar";
import { Search, ChevronRight, Home, Bell, BellDot, ShoppingBag, Settings, Users, ShoppingCart, Loader2, Share2, ChevronDown, X, CheckCircle } from 'lucide-react';
import { useTranslation } from "../../contexts/LanguageContext";
import { useAuth } from "../../App"; 

// Suggest Merchant Form Component with toggle for additional fields
const SuggestMerchantForm = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user, validateToken, httpClient } = useAuth();
  
  const [formData, setFormData] = useState({
    merchant_name: '',
    website: '',
    plan_name: '',
    price: '',
    billing_interval: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.merchant_name.trim()) {
      setError(t('marketplace.suggestions.merchantNameRequired') || 'Merchant name is required');
      return;
    }

    if (!user) {
      setError(t('marketplace.suggestions.userNotLoggedIn') || 'User not logged in');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        merchant_name: formData.merchant_name.trim(),
        ...(formData.website && { website: formData.website.trim() }),
        ...(formData.plan_name && { plan_name: formData.plan_name.trim() }),
        ...(formData.price && { price: parseFloat(formData.price) }),
        ...(formData.billing_interval && { billing_interval: formData.billing_interval })
      };

      const response = await httpClient.request('/api/suggest-merchant', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || t('marketplace.suggestions.submissionFailed') || 'Submission failed');
      }

      if (data.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          merchant_name: '',
          website: '',
          plan_name: '',
          price: '',
          billing_interval: ''
        });
        setShowAdditionalFields(false);
        
        // Close after success
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      } else {
        throw new Error(data.message || t('marketplace.suggestions.submissionFailed') || 'Submission failed');
      }
    } catch (err) {
      console.error('Error submitting merchant suggestion:', err);
      
      // Handle token expiration
      if (err.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          setError(t('marketplace.suggestions.sessionExpired') || 'Session expired. Please log in again.');
          return;
        }
        // Retry the request
        return handleSubmit(e);
      }
      
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      merchant_name: '',
      website: '',
      plan_name: '',
      price: '',
      billing_interval: ''
    });
    setShowAdditionalFields(false);
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const toggleAdditionalFields = () => {
    setShowAdditionalFields(!showAdditionalFields);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('marketplace.suggestions.title') || 'Suggest a Merchant'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <p className="text-green-800 text-sm">
                {t('marketplace.suggestions.successMessage') || 'Thank you! Your suggestion has been submitted.'}
              </p>
            </div>
          )}

          {/* Merchant Name - Always Visible */}
          <div>
            <label htmlFor="merchant_name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('marketplace.suggestions.merchantName') || 'Merchant Name'} *
            </label>
            <input
              type="text"
              id="merchant_name"
              name="merchant_name"
              value={formData.merchant_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder={t('marketplace.suggestions.merchantNamePlaceholder') || 'e.g., Netflix, Spotify'}
              disabled={loading || success}
              required
            />
          </div>

          {/* Toggle for Additional Fields */}
          <div className="pt-2">
            <button
              type="button"
              onClick={toggleAdditionalFields}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              disabled={loading || success}
            >
              <span>
                {showAdditionalFields 
                  ? (t('marketplace.suggestions.hideAdditionalFields') || 'Hide additional details') 
                  : (t('marketplace.suggestions.showAdditionalFields') || 'Add more details (optional)')
                }
              </span>
              <ChevronDown 
                className={`h-4 w-4 transition-transform ${showAdditionalFields ? 'rotate-180' : ''}`}
              />
            </button>
          </div>

          {/* Additional Fields - Conditionally Rendered */}
          {showAdditionalFields && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              {/* Website */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('marketplace.suggestions.website') || 'Website'}
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder={t('marketplace.suggestions.websitePlaceholder') || 'https://example.com'}
                  disabled={loading || success}
                />
              </div>

              {/* Plan Name */}
              <div>
                <label htmlFor="plan_name" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('marketplace.suggestions.planName') || 'Plan Name'}
                </label>
                <input
                  type="text"
                  id="plan_name"
                  name="plan_name"
                  value={formData.plan_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder={t('marketplace.suggestions.planNamePlaceholder') || 'e.g., Premium, Basic, Family'}
                  disabled={loading || success}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Price */}
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('marketplace.suggestions.price') || 'Price'}
                  </label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="0.00"
                    disabled={loading || success}
                  />
                </div>

                {/* Billing Interval */}
                <div>
                  <label htmlFor="billing_interval" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('marketplace.suggestions.billingInterval') || 'Billing Interval'}
                  </label>
                  <select
                    id="billing_interval"
                    name="billing_interval"
                    value={formData.billing_interval}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    disabled={loading || success}
                  >
                    <option value="">{t('marketplace.suggestions.selectInterval') || 'Select interval'}</option>
                    <option value="monthly">{t('marketplace.suggestions.monthly') || 'Monthly'}</option>
                    <option value="yearly">{t('marketplace.suggestions.yearly') || 'Yearly'}</option>
                    <option value="weekly">{t('marketplace.suggestions.weekly') || 'Weekly'}</option>
                    <option value="quarterly">{t('marketplace.suggestions.quarterly') || 'Quarterly'}</option>
                    <option value="one-time">{t('marketplace.suggestions.oneTime') || 'One-time'}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              {t('marketplace.suggestions.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={loading || success || !formData.merchant_name.trim() || !user}
              className="flex-1 px-4 py-2 bg-[#253985] text-white rounded-lg hover:bg-[#1a2c6d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('marketplace.suggestions.submitting') || 'Submitting...'}
                </>
              ) : success ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {t('marketplace.suggestions.submitted') || 'Submitted!'}
                </>
              ) : (
                t('marketplace.suggestions.submit') || 'Submit Suggestion'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Merchant Card Component
const MerchantCard = React.memo(({ merchant, onClick }) => {
  const { t } = useTranslation();
  
  // Find the lowest price plan with proper price handling
  const lowestPricePlan = React.useMemo(() => {
    if (merchant.plans.length > 0) {
      return merchant.plans.reduce((lowest, plan) => {
        const planPrice = Number(plan.price) || 0;
        const lowestPrice = Number(lowest.price) || 0;
        return planPrice < lowestPrice ? plan : lowest;
      }, merchant.plans[0]);
    }
    return null;
  }, [merchant.plans]);

  // Format the price display
  const formatPrice = (price) => {
    return price % 1 === 0 ? price : price.toFixed(2);
  };

  return (
    <div 
      onClick={onClick}
      className="border border-[#253985]/20 rounded-xl overflow-hidden hover:shadow-lg transition-all bg-white cursor-pointer group"
    >
      <div className="p-5 flex flex-col h-full">
        <div className="flex justify-center mb-4">
          <img 
            src={merchant.logo} 
            alt={merchant.name} 
            className="w-20 h-20 rounded-lg object-contain p-1 border border-[#253985]/10"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/80';
            }}
          />
        </div>
        
        <div className="flex-1 space-y-2">
          <h3 className="font-medium text-[#253985] text-center mb-1 text-lg group-hover:text-[#1a2c6d]">
            {merchant.name}
          </h3>
          
          {merchant.description && (
            <p className="text-sm text-gray-600 text-center line-clamp-3">
              {merchant.description}
            </p>
          )}
          
          {lowestPricePlan && (
            <div className="mt-3 text-center">
              <span className="inline-block bg-[#253985]/10 text-[#253985] px-3 py-1 rounded-full text-sm font-medium">
                {t('marketplace.from') || 'From'} {formatPrice(lowestPricePlan.price)} {lowestPricePlan.currency}/{lowestPricePlan.billing_interval}
              </span>
            </div>
          )}
        </div>

        <button 
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="mt-4 w-full py-2 bg-[#253985] hover:bg-[#1a2c6d] text-white rounded-lg text-sm font-medium transition-colors"
        >
          {t('marketplace.moreDetails') || 'More Details'}
        </button>
      </div>
    </div>
  );
});

// Main Marketplace Component
const Marketplace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(['All Categories']);
  const [sortBy, setSortBy] = useState('Popular');
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isSuggestFormOpen, setIsSuggestFormOpen] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const dropdownRef = useRef(null);

  // Memoize translation strings to prevent unnecessary re-renders
  const translatedStrings = useMemo(() => ({
    allCategories: t('marketplace.allCategories') || 'All Categories',
    popular: t('marketplace.popular') || 'Popular',
    priceLowToHigh: t('marketplace.priceLowToHigh') || 'Price: Low to High',
    priceHighToLow: t('marketplace.priceHighToLow') || 'Price: High to Low',
    categoriesSelected: t('marketplace.categoriesSelected') || 'categories selected',
    from: t('marketplace.from') || 'From',
    viewAll: t('marketplace.viewAll') || 'View All',
    noResults: t('marketplace.noResults') || 'No results found',
    noResultsDescription: t('marketplace.noResultsDescription') || 'Try selecting different categories or adjusting your search',
    searchPlaceholder: t('marketplace.searchPlaceholder') || 'Search subscriptions...',
    suggestionTitle: t('marketplace.suggestionTitle') || 'Need something else?',
    suggestionDescription: t('marketplace.suggestionDescription') || 'Suggest a subscription we should add',
    suggestButton: t('marketplace.suggestButton') || 'Suggest'
  }), [t]);

  // Initialize selectedCategories with translation after component mounts
  useEffect(() => {
    setSelectedCategories([translatedStrings.allCategories]);
    setSortBy(translatedStrings.popular);
  }, [translatedStrings.allCategories, translatedStrings.popular]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch premium status
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Use httpClient which automatically includes the auth token
      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };

  const fetchUnreadNotifications = useCallback(async () => {
    try {
      if (!user) return; // Check if user is authenticated

      // Use httpClient which automatically adds the auth token
      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        // Handle token expiration
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return fetchUnreadNotifications();
        }
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      if (err.status === 401) {
        navigate("/demo");
      }
    }
  }, [user, httpClient, validateToken, navigate]);

  // Fetch notifications with proper dependencies
  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
      const interval = setInterval(fetchUnreadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadNotifications]);

  // Check if user is authenticated
  useEffect(() => {
    if (!user) {
      navigate("/demo");
    }
  }, [user, navigate]);

  const navItems = [
    { icon: Home, label: t('bottomNav.home') || "Home", path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications') || "Notifications",
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace') || "Marketplace", path: "/marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs') || "Share Subs", path: "/share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank') || "Connect Bank", path: "/connectBank" },
    { icon: Users, label: t('bottomNav.partners') || "Partners", path: "/partners" },
    { icon: Settings, label: t('bottomNav.settings') || "Settings", path: "/account-settings" },
  ];

  // Fetch merchants from API
  const fetchMerchants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use httpClient for authenticated requests
      const response = await httpClient.request('/api/marketplace');
      
      if (!response.ok) {
        // Handle token expiration
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return fetchMerchants();
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setMerchants(data.merchants || []);
      } else {
        throw new Error(data.error || 'Failed to load marketplace');
      }
    } catch (err) {
      setError(err.message);
      console.error('Marketplace fetch error:', err);
      if (err.status === 401) {
        navigate("/demo");
      }
    } finally {
      setLoading(false);
    }
  }, [httpClient, validateToken, navigate]);

  // Fetch merchants on mount and when dependencies change
  useEffect(() => {
    if (user) {
      fetchMerchants();
      fetchPremiumStatus();
    }
  }, [user, fetchMerchants]);

  // Group merchants by category - memoize this computation
  const merchantsByCategory = useMemo(() => {
    return merchants.reduce((acc, merchant) => {
      const category = merchant.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(merchant);
      return acc;
    }, {});
  }, [merchants]);

  // Get unique categories from merchants and sort them alphabetically
  const allCategories = useMemo(() => {
    // Get all unique categories
    const categories = [...new Set(merchants.map(merchant => merchant.category))];
    
    // Sort categories alphabetically, ignoring case
    categories.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    
    // Add "All Categories" at the beginning
    return [translatedStrings.allCategories, ...categories];
  }, [merchants, translatedStrings.allCategories]);

  // Filter and sort categories based on selected categories and search query
  const filteredCategories = useMemo(() => {
    const filtered = Object.entries(merchantsByCategory)
      .filter(([category]) => 
        selectedCategories.includes(translatedStrings.allCategories) || 
        selectedCategories.includes(category)
      )
      .map(([category, merchantsInCategory]) => {
        // Filter merchants by search query
        const filteredMerchants = merchantsInCategory.filter(merchant =>
          merchant.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        return [category, filteredMerchants];
      })
      .filter(([category, filteredMerchants]) => filteredMerchants.length > 0)
      // Sort categories alphabetically, ignoring case
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

    // Sort merchants within each category
    filtered.forEach(([category, merchants]) => {
      merchants.sort((a, b) => {
        if (sortBy === translatedStrings.priceLowToHigh) {
          const aMinPrice = Math.min(...a.plans.map(p => p.price));
          const bMinPrice = Math.min(...b.plans.map(p => p.price));
          return aMinPrice - bMinPrice;
        }
        if (sortBy === translatedStrings.priceHighToLow) {
          const aMaxPrice = Math.max(...a.plans.map(p => p.price));
          const bMaxPrice = Math.max(...b.plans.map(p => p.price));
          return bMaxPrice - aMaxPrice;
        }
        if (sortBy === translatedStrings.popular && a.popularity && b.popularity) {
          return b.popularity - a.popularity;
        }
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
    });

    return filtered;
  }, [merchantsByCategory, selectedCategories, searchQuery, sortBy, translatedStrings]);

  const toggleCategory = useCallback((category) => {
    setSelectedCategories(prev => {
      const allCategoriesText = translatedStrings.allCategories;
      
      if (category === allCategoriesText) {
        if (prev.includes(allCategoriesText)) {
          return [];
        } else {
          return [allCategoriesText];
        }
      } else {
        if (prev.includes(allCategoriesText)) {
          return [category];
        } else if (prev.includes(category)) {
          const newCategories = prev.filter(c => c !== category);
          return newCategories.length === 0 ? [allCategoriesText] : newCategories;
        } else {
          return [...prev, category];
        }
      }
    });
  }, [translatedStrings.allCategories]);

  const removeCategory = useCallback((categoryToRemove) => {
    setSelectedCategories(prev => {
      const newCategories = prev.filter(category => category !== categoryToRemove);
      const allCategoriesText = translatedStrings.allCategories;
      return newCategories.length === 0 ? [allCategoriesText] : newCategories;
    });
  }, [translatedStrings.allCategories]);

  const handleCategorySelect = useCallback((category) => {
    toggleCategory(category);
  }, [toggleCategory]);

  const getDisplayText = useCallback(() => {
    const allCategoriesText = translatedStrings.allCategories;
    if (selectedCategories.length === 0 || selectedCategories.includes(allCategoriesText)) {
      return allCategoriesText;
    }
    if (selectedCategories.length === 1) {
      return selectedCategories[0];
    }
    return selectedCategories.length + ' ' + translatedStrings.categoriesSelected;
  }, [selectedCategories, translatedStrings]);

  return (
    <div className="flex h-screen bg-gray-50">
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={location.pathname}
        navigate={navigate}
        user={user}
        onLogout={logout}
      />

      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />
        
        <div className="p-6 space-y-6">
          {/* Header with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-6 rounded-lg text-center transition-all duration-500`}>
            <h1 className="text-xl font-semibold mb-2">{t('marketplace.title') || 'Subscription Marketplace'}</h1>
            <p className="text-white">{t('marketplace.subtitle') || 'Discover and manage your favorite subscriptions'}</p>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={translatedStrings.searchPlaceholder}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              {/* Category Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                  className="flex items-center justify-between w-64 pl-3 pr-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                >
                  <span className="truncate">{getDisplayText()}</span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isCategoryDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                    {allCategories.map(category => (
                      <div
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                          selectedCategories.includes(category) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${
                          selectedCategories.includes(category) 
                            ? 'bg-[#253985] border-[#253985]' 
                            : 'border-gray-300'
                        }`}>
                          {selectedCategories.includes(category) && (
                            <div className="w-2 h-2 bg-white rounded-sm" />
                          )}
                        </div>
                        <span className={selectedCategories.includes(category) ? 'text-[#253985] font-medium' : ''}>
                          {category}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value={translatedStrings.popular}>{translatedStrings.popular}</option>
                  <option value={translatedStrings.priceLowToHigh}>{translatedStrings.priceLowToHigh}</option>
                  <option value={translatedStrings.priceHighToLow}>{translatedStrings.priceHighToLow}</option>
                </select>
                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none transform rotate-90" />
              </div>
            </div>
          </div>

          {/* Selected Categories Tags */}
          {selectedCategories.length > 0 && !(selectedCategories.length === 1 && selectedCategories[0] === translatedStrings.allCategories) && (
            <div className="flex flex-wrap gap-2 mb-6">
              {selectedCategories.map(category => (
                <div
                  key={category}
                  className="bg-[#253985] text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
                >
                  {category}
                  <button
                    onClick={() => removeCategory(category)}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Content Area */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-medium">{t('marketplace.loadErrorTitle') || 'Error loading marketplace'}</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
              >
                {t('marketplace.tryAgain') || 'Try again'}
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-8">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map(([category, merchantsInCategory]) => (
                    <div key={category} className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">{category}</h2>
                        <button
                          onClick={() => navigate(`/marketplace-category/${encodeURIComponent(category)}`)}
                          className="text-sm text-[#253985] hover:text-[#1a2c6d] font-medium flex items-center"
                        >
                          {translatedStrings.viewAll} <ChevronRight className="h-4 w-4 ml-1" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {merchantsInCategory.map(merchant => (
                          <MerchantCard 
                            key={merchant.id} 
                            merchant={merchant}
                            onClick={() => navigate(`/merchant/${merchant.id}`)}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 text-center">
                    <h2 className="text-lg font-semibold mb-2 text-gray-800">{translatedStrings.noResults}</h2>
                    <p className="text-gray-600">{translatedStrings.noResultsDescription}</p>
                  </div>
                )}
              </div>

              {/* Suggestion Section */}
              <div className="sticky bottom-6 mt-6 bg-white p-4 rounded-xl shadow-md border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-800">{translatedStrings.suggestionTitle}</h3>
                    <p className="text-sm text-gray-500">{translatedStrings.suggestionDescription}</p>
                  </div>
                  <button
                    onClick={() => setIsSuggestFormOpen(true)}
                    className="bg-[#253985] hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    {translatedStrings.suggestButton}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Suggest Merchant Form Modal with toggle functionality */}
        <SuggestMerchantForm 
          isOpen={isSuggestFormOpen}
          onClose={() => setIsSuggestFormOpen(false)}
        />
      </div>
    </div>
  );
};

export default Marketplace;